/**
 * Category Exclusion API for Electron
 * Manages category-based domain exclusions for context collection
 *
 * TypeScript is the single source of truth for enabled state.
 * Swift layer only maintains category definitions and applies enabled state sent from TypeScript.
 */

import {
  type CategoryID,
  type CategoryInfo,
} from '../../../../electron/shared/types';
import { ElectronAPI } from '../base/ElectronAPI';

export class CategoryExclusionAPI extends ElectronAPI {
  /**
   * Get all category definitions (without enabled state)
   * Enabled state is managed by TypeScript in ExclusionManager
   */
  async getCategoryExclusions(): Promise<{
    success: boolean;
    categories?: CategoryInfo[];
    error?: string;
  }> {
    try {
      return await this.invoke<{
        success: boolean;
        categories?: CategoryInfo[];
        error?: string;
      }>('get-category-exclusions');
    } catch (error) {
      console.error('Failed to get category exclusions:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Get enabled category IDs from ExclusionManager
   * Returns the list of category IDs that are currently enabled
   */
  async getEnabledCategoryIds(): Promise<CategoryID[]> {
    return this.invoke<CategoryID[]>('get-enabled-category-ids');
  }

  /**
   * Update enabled category exclusions
   * @param enabledCategoryIds Array of category IDs to enable
   */
  async updateCategoryExclusions(
    enabledCategoryIds: CategoryID[]
  ): Promise<{ success: boolean; error?: string }> {
    try {
      return await this.invoke<{ success: boolean; error?: string }>(
        'update-category-exclusions',
        enabledCategoryIds
      );
    } catch (error) {
      console.error('Failed to update category exclusions:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}
